// @ts-strict-ignore
import type { PlanDrawerViewType } from "@clinic/shared/reactiveVar/usePlanWriterPlanDrawerVar/usePlanWriterPlanDrawerVar";
import type { ProductSwapAvailabilityStatus } from "@shared/types/availabilityStatus";
import type { VimeoEmbedUrl } from "@shared/types/vimeo";
import type { MetricsType } from "@shared/utils/AnalyticsDashboardPage/AnalyticsDashboardTypes";
import { isProduction } from "@shared/utils/fullscriptEnv/fullscriptEnv";

interface VideoLinks {
  [key: string]: VimeoEmbedUrl;
}

const accountSetupMenuVideos: VideoLinks = {
  setupProfitAccount: "https://player.vimeo.com/video/1098613844",
  addPractitionersAndStaff: "https://player.vimeo.com/video/1103837179",
  registerForLabs: "https://player.vimeo.com/video/1103832710",
};

const clinicRoutes = {
  base: "/o",
  careCenter: {
    root: "/o/care-center",
    products: "/o/care-center/products",
    productDetail: "/o/care-center/products/:productId",
    getProductsPath: (planId?: string, pracId?: string, drawerViewType?: PlanDrawerViewType) => {
      const route = "/o/care-center/products";
      const params = [];

      if (!planId && !pracId) {
        return route;
      }

      if (planId) {
        params.push(`treatmentPlanId=${planId}`);
      }

      if (pracId) {
        params.push(`practitioner_id=${pracId}`);
      }

      if (drawerViewType) {
        params.push(`drawerOpen=${drawerViewType}`);
      }

      return `${route}?${params.join("&")}`;
    },
    getPathWithSearchParams: (
      redirectUrl: string,
      planId?: string,
      pracId?: string,
      labComparisons?: string
    ) => {
      const params = [];

      if (!planId && !pracId && !labComparisons) {
        return redirectUrl;
      }

      if (planId) {
        params.push(`treatmentPlanId=${planId}`);
      }

      if (pracId) {
        params.push(`practitioner_id=${pracId}`);
      }

      if (labComparisons) {
        params.push(`comparisons=${labComparisons}`);
      }

      return `${redirectUrl}?${params.join("&")}`;
    },
    getProductDetailPath: (id: string) => `/o/care-center/products/${id}`,
    productDetailVariant: "/o/care-center/products/:productId/:variantId",
    getProductDetailVariantPath: (productId: string, variantId: string) =>
      `/o/care-center/products/${productId}${variantId ? `/${variantId}` : ""}`,
    productDetailsOptimizedProducts:
      "/o/care-center/products/:productId/:variantId/optimized-products",
    getProductDetailsOptimizedProducsPath: (productId: string, variantId: string) =>
      `/o/care-center/products/${productId}${variantId ? `/${variantId}` : ""}/optimized-products`,
    compareProducts: "/o/care-center/products/:productId/:variantId/compare",
    getCompareProductsPath: (productId: string, variantId: string) =>
      `/o/care-center/products/${productId}${variantId ? `/${variantId}` : ""}/compare`,
    resources: "/o/care-center/resources",
    labs: "/o/care-center/labs",
    getLabsPath: (planId?: string, practitionerId?: string, autoStartRegistration?: boolean) => {
      const params = [];

      if (planId) {
        params.push(`treatmentPlanId=${planId}`);
      }

      if (practitionerId) {
        params.push(`practitioner_id=${practitionerId}`);
      }

      if (autoStartRegistration) {
        params.push(`autoStartRegistration=true`);
      }

      return `/o/care-center/labs${params.length > 0 ? `?${params.join("&")}` : ""}`;
    },
    getLabsAutoStartPath: () => "/o/care-center/labs?autoStartRegistration=true",
    getLabsTemplatePath: (planId: string) =>
      `/o/care-center/labs?treatmentPlanId=${planId}&drawerOpen=templateSelectionView`,
    labsCompare: "/o/care-center/labs/compare",
    journeys: "/o/care-center/labs/journeys",
    journeysAuthNetwork: "https://support.fullscript.com/articles/authorization-network/",
    connectedCareTools: "https://fullscript.com/lp/stronger-patient-connections",
    labcorpInstructions:
      "https://support.fullscript.com/articles/completing-your-access-lab-test-with-labcorp/",
    labsCustomPanel: {
      view: (id: string) => `/o/care-center/labs/panel/${id}`,
      edit: (id: string) => `/o/care-center/labs/panel/${id}/edit`,
      editViewTest: (id: string, testId: string) =>
        `/o/care-center/labs/panel/${id}/edit/tests/${testId}`,
    },
    labsTemplatesAndPanels: "/o/care-center/labs/templates-and-panels",
    labsSettings: "/o/care-center/labs_settings",
    labsToolkit: "https://fullscript.com/lp/labs-toolkit",
    labsPatientExperience: "https://fullscript.com/labs-patient-experience",
    getTestDetailPath: (id: string, parentTestId?: string) =>
      `/o/care-center/labs/${id}${parentTestId ? `?parentTestId=${parentTestId}` : ""}`,
    getTestDetailAddOnPath: (id: string) => `/o/care-center/labs/${id}?isAddOnExpanded=true`,
    resourcesMyUploadDetails: "/o/care-center/resources/details/my-upload/:id",
    getResourcesMyUploadDetails: (id: string) => `/o/care-center/resources/details/my-upload/${id}`,
    resourcesFSDetails: "/o/care-center/resources/details/fs/:id",
    getResourcesFSDetails: (id: string) => `/o/care-center/resources/details/fs/${id}`,
    resourcesUpload: "/o/care-center/resources/upload",
    dailyPacks: "/o/care-center/daily-packs",
    dailyPacksBuilder: "/o/care-center/daily-packs/build",
    dailyPackPDP: "/o/care-center/daily-packs/pack/:variantId",
    getDailyPackPDP: (variantId: string) => `/o/care-center/daily-packs/pack/${variantId}`,
    dailyPackPill: "/o/care-center/daily-packs/pill/:pillId",
    getDailyPackPillPDP: (pillId: string) => `/o/care-center/daily-packs/pill/${pillId}`,
    getDailyPackPath: (redirectUrl: string, dailyPackId: string) => {
      const params = [];
      params.push(`dailyPack_id=${dailyPackId}`);

      return `${redirectUrl}?${params.join("&")}`;
    },
    templates: {
      base: "/o/care-center/templates",
      view: (id: string, source?: string) =>
        `/o/care-center/templates/${id}${source ? `?source=${source}` : ""}`,
      getTemplatesPath: (planId: string) => `/o/care-center/templates?treatmentPlanId=${planId}`,
    },
    planWriting: "/o/care-center/plan-writing",
    getPlanWritingPath: (planId: string, pracId?: string) => {
      let pracIdQueryParam = "";
      if (pracId) {
        pracIdQueryParam = `&practitioner_id=${pracId}`;
      }

      return `/o/care-center/plan-writing?treatmentPlanId=${planId}${pracIdQueryParam}`;
    },
    getProtocolWritingPath: (protocolId: string, pracId?: string) => {
      let pracIdQueryParam = "";
      if (pracId) {
        pracIdQueryParam = `&practitioner_id=${pracId}`;
      }

      return `/o/care-center/plan-writing?protocolId=${protocolId}${pracIdQueryParam}`;
    },
    favorites: `/o/care-center/favorites`,
    categorySpotlight: {
      learnMoreLink:
        "https://fullscript.com/blog/menopause-support?utm_campaign=WD_2025-06_Menopause-Clinmerch&utm_medium=referral&utm_source=FullscriptApp",
    },
  },
  restrictionUpdates: {
    detailPage: "/o/restriction-updates/:changeLogId",
  },
  // THROWAWAY: dev-only harness for the FS-Scribe M1 (INO-1220) dogfood proof.
  // Delete this path constant along with clinic/scribe/_m1DevHarness/ once
  // Jan's real Rails surfaces land.
  scribeDevHarness: "/o/scribe/dev-harness",
  productSwapsV2: {
    root: "/o/product-swaps",
    listPage: "/o/product-swaps/:status",
    getListPage: (
      status: ProductSwapAvailabilityStatus,
      params?: { practitionerId?: string; sort?: string }
    ) => {
      const baseUrl = `/o/product-swaps/${status}`;
      if (!params) return baseUrl;

      const queryParams = [];
      if (params.practitionerId) queryParams.push(`practitionerId=${params.practitionerId}`);
      if (params.sort) queryParams.push(`sort=${params.sort}`);

      return queryParams.length ? `${baseUrl}?${queryParams.join("&")}` : baseUrl;
    },
    detailPage: "/o/product-swaps/:status/:productId/:variantId",
    getDetailPage: (
      status: ProductSwapAvailabilityStatus,
      productId: string,
      variantId: string,
      practitionerId?: string
    ) =>
      `/o/product-swaps/${status}/${productId}/${variantId}${practitionerId ? `?practitionerId=${practitionerId}` : ""}`,
  },
  wholesale: {
    root: "/o/wholesale",
    shop: "shop",
    shopFullPath: () => `${clinicRoutes.wholesale.root}/${clinicRoutes.wholesale.shop}`,
    orders: "orders",
    ordersFullPath: () => `${clinicRoutes.wholesale.root}/${clinicRoutes.wholesale.orders}`,
    reorders: "reorders",
    reordersFullPath: () => `${clinicRoutes.wholesale.root}/${clinicRoutes.wholesale.reorders}`,
    favorites: "favorites",
    favoritesFullPath: () => `${clinicRoutes.wholesale.root}/${clinicRoutes.wholesale.favorites}`,
    products: "products",
    labSupplies: "lab-supplies",
    labSuppliesFullPath: () =>
      `${clinicRoutes.wholesale.root}/${clinicRoutes.wholesale.labSupplies}`,
    productsFullPath: (productId?: string) => {
      const basePath = `${clinicRoutes.wholesale.root}/${clinicRoutes.wholesale.products}`;
      if (productId) {
        return `${basePath}/${productId}`;
      }

      return basePath;
    },
    labKitDetails: (labKitId: string) =>
      `${clinicRoutes.wholesale.root}/${clinicRoutes.wholesale.labKits}/${labKitId}`,
    labKits: "lab-kits",
    labKitsFullPath: (labKitId?: string, practitionerId?: string) => {
      const basePath = `${clinicRoutes.wholesale.root}/${clinicRoutes.wholesale.labKits}`;
      if (labKitId) {
        return `${basePath}/${labKitId}${practitionerId ? `?practitioner_id=${practitionerId}` : ""}`;
      }

      return basePath;
    },
    policies: () => `${clinicRoutes.wholesale.root}/policies`,
    swapVariant: variantId => `${clinicRoutes.wholesale.shopFullPath()}?swap="${variantId}"`,
  },
  manage: {
    favorites: "/o/manage/favorites",
  },
  home: "/o/home",
  sessions: "/o/sessions",
  sessionDetails: (sessionId: string) => `/o/sessions/${sessionId}`,
  onboarding: "/o/onboarding",
  activateLabKit: ({ patientId, code }: { patientId?: string; code?: string } = {}) => {
    const params = new URLSearchParams();

    if (patientId) params.set("patientId", patientId);
    if (code) params.set("code", code);

    const queryString = params.toString();

    return queryString ? `/activate-kit?${queryString}` : "/activate-kit";
  },
  catalog: "/o/catalog",
  catalogProduct: "/o/catalog/products",
  promotions: "/o/catalog/promotions",
  checkout: "/o/checkout",
  dailyPacksInfo: "https://fullscript.com/lp/fullscript-daily-packs",
  wholesale_orders: "/o/wholesale_orders",
  naturalPartnersOrders: "/o/wholesale_orders/np",
  treatment_plans: "/o/treatment_plans",
  treatment_plans_new: "/o/treatment_plans/new",
  supplement_plans: "o/supplement_plans",
  patients: "/o/patients",
  patients_import: "/o/patients/import",
  patients_orders: "/o/patients/orders",
  patients_lab_appointments: "/o/patients/lab-appointments",
  patients_lab_orders: "/o/patients/lab-orders",
  patients_lab_orders_scheduled_plans:
    "/o/patients/lab-orders?sort=recently_updated&view=scheduled_plans",
  patients_lab_orders_draft_plans: "/o/patients/lab-orders?sort=recently_updated&view=draft_plans",
  journeysOnboarding: {
    create: "/o/journeys/create",
    preview: "/o/journeys/preview",
    personalize_storefront: "/o/journeys/personalize_storefront",
    profile: "/o/journeys/profile",
    share: "/o/journeys/share",
    previewPatientExperienceVideoLink:
      "https://www.youtube.com/embed/-COYX0w4vb8?si=lcg1BJYfl4g6gvxf",
    shareMarketingVideoLink: "https://public-assets.fullscript.com/_assets/journeys-onboarding.mp4",
    marketingToolkit: "https://fullscript.com/journeys-marketing-toolkit",
    blandSignatureSeries:
      "https://fullscript.com/lp/dr-bland-signature-series?utm_campaign=bland-signature&utm_medium=In-app&utm_source=in-app",
  },
  journey_orders: "/o/journey-orders",
  patientJourneyReview: (patientId, recommendationId) =>
    `/o/patients/${patientId}?journeyRecommendationId=${recommendationId}`,
  rupaMoveToolkitS1: "https://fullscript.com/lp/rupa-move-toolkit-s1",
  rupaMoveToolkitS2: "https://fullscript.com/lp/rupa-move-toolkit-s2",
  rupaAlertsTab: "https://labs.rupahealth.com/?tabs=alerts",
  product_swapping: "/o/product_swapping", // NOTE: Old product swap route
  swapPages: {
    // NOTE: Old product swap routes
    current: "/o/product_swapping/current",
    success: "/o/product_swapping/success",
    past: "/o/product_swapping/past",
  },
  protocols: "/o/protocols",
  protocols_new: "/o/protocols/new",
  protocols_fullscript: "/o/protocols/fullscript",
  choose_practitioner: "/o/choose_practitioner",
  choosePractitionerWithRedirectUrl: (redirectUrl: string, parameters: URLSearchParams) =>
    `/o/choose_practitioner?redirect_url=${redirectUrl}&redirect_params=${parameters.toString()}`,
  reports: "/o/reports",
  payouts_report: "/o/reports/payouts_report",
  earnings_report: "/o/reports/earnings_report",
  choose_earnings_report: "/o/reports/earnings_report/choose_earnings_report",
  choose_payout_dates: "/o/reports/earnings_report/choose_payout_dates",
  review_earnings_report: "/o/reports/earnings_report/review_earnings_report",
  downloadEarningReport: (reportType: string, storeLegacyId: number) =>
    `/o/payable/spree-store!${storeLegacyId}/${reportType}.xlsx`,
  staffs: "/c/staffs", // even though this is /c/ its accessible to pracs as well
  logout: "/logout",
  lab_recommendations: "/o/lab_recommendations",
  labsBookCall: (practitionerInfo?: { firstName?: string; lastName?: string; email?: string }) => {
    const baseUrl = "https://calendly.com/d/ckrn-48k-pqk";

    const fullName = [practitionerInfo?.firstName, practitionerInfo?.lastName]
      .filter(Boolean)
      .join(" ");

    const params = new URLSearchParams();
    if (fullName) params.set("a1", fullName);
    if (practitionerInfo?.email) params.set("a2", practitionerInfo.email);

    const queryString = params.toString().replaceAll("+", "%20");

    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  },
  support: "https://support.fullscript.com",
  fsTOS: "https://fullscript.com/terms-of-service",
  scheduledAdherenceSurveys: "https://support.fullscript.com/articles/scheduled-adherence-surveys/",
  globalPatientAccessSettings:
    "https://support.fullscript.com/articles/global-patient-access-settings/",
  productCatalogPermissions:
    "https://support.fullscript.com/articles/catalog-permissions-and-access-settings/",
  onSiteProducts: "https://support.fullscript.com/articles/on-site-products/",
  wellevateMigrationSupport:
    "https://support.fullscript.com/hc/en-us/sections/13558530393115-Wellevate-migration",
  wellevateMigrationToolkit: "https://fullscript.com/lp/migration-toolkit-int",
  wellevatePatientSupportVideoLink: "https://www.youtube.com/embed/ZIuUFx1g6wY",
  wellevateLoyalistSupportVideoLink: "https://www.youtube.com/embed/9WgeUi92ECw",
  labsAuthNetworkLink: "https://support.fullscript.com/articles/authorization-network/",
  labsAuthNetworkRupaMigrationLink:
    "https://support.fullscript.com/articles/moving-from-rupa-to-fullscript-lab-ordering-access-and-the-authorization-network/",
  favorites: "/o/favorites",
  favoriteCategory: favoriteCategoryId => `/o/favorites/${favoriteCategoryId}`,
  welcome: "/welcome",
  whats_new: "https://fullscript.com/whats-new",
  integrations: "/o/integrations",
  libraries: "/o/libraries",
  storeSettings: {
    base: "/o/store_settings",
    dispensary: "/o/store_settings/dispensary",
    integrations: "/o/store_settings/integrations",
    account: "/o/store_settings/account_settings",
    staff: "/o/store_settings/staff_settings",
    features: "/o/store_settings/features",
    landingPage: "/o/store_settings/landing_page",
    payments: "/o/store_settings/payments",
    paymentsEdit: "/o/store_settings/payments/edit",
    paymentsPayouts: "/o/store_settings/payments/payouts",
    paymentsTaxEdit: "/o/store_settings/payments/tax/edit",
    accountPaymentsInfo: "/o/store_settings/account_settings/payment_info",
    accountSecurity: "/o/store_settings/account_settings/security",
    accountPreferences: "/o/store_settings/account_settings/preferences",
    accountBasicInfo: "/o/store_settings/account_settings/basic_info",
    accountConnectedAccounts: "/o/store_settings/account_settings/connected_accounts",
    accountStorefrontProfile: "/o/store_settings/account_settings/profile",
    assistSettings: "/o/store_settings/account_settings/assist_settings",
    assistSettingsConnections: "/o/store_settings/account_settings/assist_settings/connections",
    permissions: "/o/store_settings/permissions",
    permissionsPatient: "/o/store_settings/permissions/patient",
    promotions: "/o/store_settings/promotions",
    storeInfo: "/o/store_settings/dispensary_info",
    websiteButtons: "/o/store_settings/website_buttons",
    labs: "/o/store_settings/labs",
    labsPayments: "/o/store_settings/labs/payment",
    labsOrderingAccess: "/o/store_settings/labs/ordering_access",
    labsPhlebotomy: "/o/store_settings/labs/phlebotomy",
    labsResults: "/o/store_settings/labs/results",
    privacy: "/o/store_settings/privacy",
    storefrontProfile: "/o/store_settings/profile",
    wholesale: "/o/store_settings/wholesale",
  },
  analyticsDashboard: {
    root: "/o/analytics-dashboard",
    detailPage: "/o/analytics-dashboard/:metrics",
    getDetailPage: (metrics: MetricsType) => `/o/analytics-dashboard/${metrics}`,
    protocols: "/o/analytics-dashboard/protocols",
    mpp: "/o/analytics-dashboard/mpp",
    ordersPlacedHandoutLink:
      "https://fs-marketing-files.s3.amazonaws.com/handouts/fullscript-mobile-app-2025.pdf",
    plansSectionHandoutLink: "https://fullscript.com/blog/writing-more-engaging-treatment-plans",
    individualPlanConversionRateHandoutLink:
      "https://support.fullscript.com/articles/similar-products-for-practitioners/",
    educationalVideoLink: "https://www.youtube.com/embed/r7xyecWMFcY",
    firstTimeAutoRefillsHandoutLink:
      "https://support.fullscript.com/articles/autorefills-save-discount/",
    autoRefillOrdersHandoutLink:
      "https://fs-marketing-files.s3.amazonaws.com/handouts/auto-refill-education.pdf",
    profitMarginLearnMoreLink: "https://support.fullscript.com/articles/profit-dispensaries/",
    revenueCalculatorCALink: "https://fullscript1.outgrow.us/ca-fullscript-revenue-calculator-2025",
    revenueCalculatorUSLink: "https://fullscript1.outgrow.us/fullscript-revenue-calculator-2025",
  },
  planSentHandouts: {
    fullscriptVsAmazon:
      "https://fs-marketing-files.s3.us-east-1.amazonaws.com/handouts/fullscript-vs-amazon.pdf",
  },
  accountSettings: "/o/account_settings",
  accountSettingsBasicInfo: "/o/account_settings/basic_info",
  accountSettingsSecurity: "/o/account_settings/security",
  accountSettingsPreferences: "/o/account_settings/preferences",
  accountSettingsPaymentInfo: "/o/account_settings/payment_info",
  accountSettingsConnectedAccounts: "/o/account_settings/connected_accounts",
  accountSettingsStorefrontProfile: "/o/account_settings/profile",
  accounts: "/accounts",
  stopImpersonating: "/a/sessions/stop_impersonating",
  integrationDetails: integrationId => `/o/integrations/${integrationId}`,
  integrationsSupport: "https://support.fullscript.com/sections/integrations/",
  newTreatmentPlanForPatient: patientId => `/o/treatment_plans/new?patient_id=${patientId}`,
  choosePractitionerBase: "/o/choose_practitioner",
  choosePractitioner: patientId => `/o/choose_practitioner?patient_id=${patientId}`,
  product: productId => `/o/catalog/products/${productId}`,
  productWithVariant: (productId, variantId) => `/o/catalog/products/${productId}/${variantId}`,
  swapVariant: masterVariantId => `/o/catalog?swap="${masterVariantId}"`,
  officeTreatmentPlansEdit: treatmentPlanId => `/o/treatment_plans/${treatmentPlanId}/edit`,
  patient: id => `/o/patients/${id}`,
  patientWithSuccessBannerParams: (
    id: string,
    { legacyId, treatmentPlanId }: { legacyId: number; treatmentPlanId: string }
  ) =>
    `/o/patients/${id}?showSuccessBanner=true&legacyId=${legacyId}&treatmentPlanId=${treatmentPlanId}`,
  patientTreatmentPlans: id => `/o/patients/${id}/treatment_plans`,
  patientSupplementPlan: id => `/o/patients/${id}/current_supplements`,
  printSupplementPlan: id => `/o/supplement_plans/${id}?print=true`,
  patientLabs: id => `/o/patients/${id}/labs`,
  patientHealthMetrics: (patientId: string) => `/o/patients/${patientId}/health-metrics`,
  patientLabTrends: (patientId: string) => `/o/patients/${patientId}/labs/trends`,
  patientLabRecommendationDetails: (patientId, labRecommendationId) =>
    `/o/patients/${patientId}/labs/${labRecommendationId}`,
  patientLabRecommendationResultDetails: (patientId: string, labRecommendationId: string) =>
    `/o/patients/${patientId}/labs/${labRecommendationId}/results`,
  patientUploadedLabResultDetails: (patientId: string, uploadedLabResultId: string) =>
    `/o/patients/${patientId}/uploaded-labs/${uploadedLabResultId}/results`,
  patientUploadedLabResultReview: (patientId: string, uploadedLabResultId: string) =>
    `/o/patients/${patientId}/uploaded-labs/${uploadedLabResultId}/review`,
  patientLabRecommendation: (labRecommendationId: string) =>
    `/u/labs/recommendation/${labRecommendationId}`,
  patientOrders: id => `/o/patients/${id}/orders`,
  patientStart: id => `/o/patients/${id}/start`,
  patientsEdit: id => `/o/patients/${id}/edit`,
  patientsCheckout: id => `/o/patients/${id}/checkout`,
  protocolsTab: tabId => `/o/protocols/${tabId}`,
  startTreatmentPlanCheckout: (legacyPatientId, legacyTreatmentPlanId) =>
    `/o/patients/${legacyPatientId}/checkout/populate_with_treatment_plan.html?reset_order=true&treatment_plan_id=${legacyTreatmentPlanId}`,
  patientsTreatmentPlanInfo: (patientId, treatmentPlanId) =>
    `/o/patients/${patientId}/treatment_plans/${treatmentPlanId}`,
  patientPageWithTreatmentPlan: (patientId, treatmentPlanId) =>
    `/o/patients/${patientId}?treatment_plan=${treatmentPlanId}`,
  patientsOrderInfo: (patientId, orderId) => `/o/patients/${patientId}/orders/${orderId}`,
  peerReferrals: "/o/peer_referrals",
  orderPdfLink: orderNumber => `/o/orders/${orderNumber}.pdf`,
  orderPrintLink: orderNumber => `/o/orders/${orderNumber}.html?print=true`,
  orderLabsPdfLink: orderNumber => `/o/orders/${orderNumber}.pdf?labs=true`,
  orderLabsPrintLink: orderNumber => `/o/orders/${orderNumber}.html?print=true&labs=true`,
  staffEdit: id => `/c/staffs/${id}/edit`,
  wellevateMigration: "/o/wellevate_migration",
  buildPlan: "/o/build_plan",
  buildPlanMyTemplates: "/o/build_plan/my_templates",
  buildPlanFullscriptCreated: "/o/build_plan/fullscript_created_templates",
  plansPageWithTab: "/o/plans/:tabId",
  plansPageIndividual: `/o/plans/individual_plans`,
  plansPageMultiPatient: `/o/plans/multi_patient`,
  plansPageTemplate: `/o/plans/templates`,
  plansPageTemplateComposition: (composition: string) =>
    `/o/plans/templates?composition=${composition}`,
  plansPage: "/o/plans",
  plansNew: "/o/plans/new",
  plansNewForPatient: patientId => `/o/plans/new?patient_id=${patientId}`,
  plansNewIndividual: "/o/plans/new?plan_type=individual",
  plansNewMultipatient: "/o/plans/new?plan_type=multi_patient",
  plansNewTemplate: "/o/plans/new?plan_type=template",
  officePlansEdit: (treatmentPlanId: string, pracId?: string) => {
    let pracIdQueryParam = "";
    if (pracId) {
      pracIdQueryParam = `?practitioner_id=${pracId}`;
    }

    return `/o/plans/${treatmentPlanId}/edit${pracIdQueryParam}`;
  },
  planSent: (treatmentPlanId: string) => `/o/plans/${treatmentPlanId}/sent`,
  newInOfficeCart: id => `/b/patients/${id}/checkout/cart`,
  checkoutAddress: patientId => `/b/patients/${patientId}/checkout/address`,
  newFullscriptExperience: "https://fullscript.com/lp/new-fullscript-us",
  qualityProgram: {
    tested: "https://fullscript.com/lp/tested-badge",
    preferred: "https://fullscript.com/lp/quality-badge#top-pick-badge",
  },
  emersonSwitch: "/user/platform-redirect?target_platform_name=emerson",
  accountSetupMenuVideos,
  gettingYourCredentialsApproved:
    "https://support.fullscript.com/articles/getting-your-credentials-approved/",
  phlebotomyMap: "/o/labs/phlebotomy-map",
  sideNav: {
    home: "/o/home",
    patients: "/o/patients",
    labs: {
      catalog: "/o/labs/catalog",
      orders: "/o/labs/orders",
    },
    products: {
      catalog: "/o/products/catalog",
      templates: {
        base: "/o/products/templates",
        view: (id: string) => `/o/products/templates/${id}`,
        getTemplatesPath: (planId: string) => `/o/products/templates?treatmentPlanId=${planId}`,
      },
      orders: "/o/products/orders",
    },
    analytics: "/o/analytics-dashboard",
    myTools: {
      base: "/o/tools/:tabId",
      templates: "/o/tools/templates",
      multiPatientPlans: "/o/tools/multi_patient",
      resources: "/o/tools/resources",
      clerkTemplates: "/o/plans/:tabId",
    },
    wholesale: {
      labKits: "/o/wholesale/lab_kits",
      products: "/o/wholesale/catalog",
      orders: "/o/wholesale/orders",
    },
    settings: "/o/store_settings/dispensary",
    integrationSettings: "/o/store_settings/integrations",
    stopImpersonating: "/a/sessions/stop_impersonating",
  },
  fullscriptAssist: "https://fullscript.com/lp/fullscript-assist",
  getFullscriptAcademyUrl: () =>
    isProduction()
      ? "https://academy.fullscript.com/home"
      : "https://fs-academy-staging.s.fullscript.io/home",
  labsBilling: "https://support.fullscript.com/articles/labs-billing-and-payment",
  inOfficeLabKits: "https://support.fullscript.com/articles/in-office-lab-kits/",
  inOfficePhlebotomy: "https://support.fullscript.com/articles/in-office-phlebotomy",
  phlebotomyOnFullscript: "https://support.fullscript.com/articles/phlebotomy-on-fullscript/",
  storefront: {
    base: "/o/storefront",
    overview: "/o/storefront/overview",
    managingStorefrontArticle:
      "https://support.fullscript.com/articles/my-storefront-managing-your-patient-facing-supplement-store",
    labs: "/o/storefront/labs",
    labsCatalog: "/o/storefront/labs/catalog",
    journeys: "/o/storefront/journeys",
    journeysCatalog: "/o/storefront/journeys/catalog",
    collections: "/o/storefront/collections",
    protocols: "/o/storefront/protocols",
    preferences: {
      base: "/o/storefront/preferences",
      branding: "/o/storefront/preferences/branding",
      promotions: "/o/storefront/preferences/promotions",
      access: "/o/storefront/preferences/access",
      discounts: "/o/store_settings/payments",
      profile: "/o/storefront/preferences/profile",
      websiteButtons: "/o/storefront/preferences/website-buttons",
    },
  },
  orchestrator: {
    conversations: "/orchestrator/api/conversations",
    conversation: (id: string) => `/orchestrator/api/conversations/${id}`,
    conversationSeen: (id: string) => `/orchestrator/api/conversations/${id}/seen`,
    assistEvents: "/orchestrator/api/assist_events",
    dictation: "/orchestrator/api/transcriptions/dictation",
    preferences: "/orchestrator/api/preferences",
    preferenceInputs: "/orchestrator/api/preferences/inputs",
    preferenceInput: (id: string) => `/orchestrator/api/preferences/inputs/${id}`,
  },
  promis10InfoPDF:
    "https://library.iterable.com/2787/15419/9032a0da06d141ffbe0454802b8d473b-fullscript-promis10-handout.pdf",
  wholesaleCanadaLearnMore: "https://fullscript.com/blog/wholesale-ordering-available-in-canada",
} as const;

export { clinicRoutes };
